import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import CommonCard from "../../components/common_card";
import { TypographyTitle, TypographyBold } from "../../style/typography";
import ReportTimeline from "../../components/arrowbox";
import DamReportStepper from "../../components/dam_report_stepper";
import MailConfirm from "../../components/mail_confirm";
import ApprovalStatusInfo from "../../components/approval_status_info";
import ReportCountdown from "../../components/report_countdown";
import ReportStopDialog from "../../components/report_stop_dialog";
import SendReportDialog from "../../components/send_report_dialog";
import { BoxCenterStart } from "../../style/box";
import { BaseDialog } from "../../components/base_dialog";
import { useAuth } from "../../contexts/AuthContext";
import { APPROVAL_STATUS, ROLE_UUID, REPORTING_TIME } from "../../const/config";
import {
  FirstReportStep01,
  FirstReportStep02,
  FirstReportStep03,
  FirstReportStep04,
  FirstReportStep05,
  FirstReportAfterApprovalTask,
} from "../../components/workflow_step";
import ErrorScreen from "../../components/error_screen";
import { commonStyles } from "../../style/common";
import { getApprovalStatus } from "../../func/status_utils";
import SkeletonLoading from "../../components/skeleton_loading";
import { useFirstReportLogic } from "../../hooks/use_first_report_logic";
import { FIRST_REPORT_TEXTS, FIRST_REPORT_POST_SEND_TASKS_TEXTS } from "../../const/inspection_texts";
import { getIntensityPriority } from '../../func/util';
import { postData } from "../../func/api";

const FirstReport = () => {
  const { uuid } = useParams();
  const { currentUser } = useAuth();

  // State
  const {
    earthquake,
    setEarthquake,
    checkResult,
    setCheckResult,
    approval,
    mailInfo,
    loading,
    open,
    setOpen,
    isChecked,
    setIsChecked,
    createComplete,
    setCreateComplete,
    mandatoryFacilitys,
    setMandatoryFacilitys,
    facilityDams,
    setFacilityDams,
    taskStatus,
    setTaskStatus,
    approvalStatus,
    isInputter,
    isReviewer,
    isReadOnly,
    isApprovedNotSent,
    isSent,
    isValidTask,
    handleRestart,
    reportDetailList,
    department
  } = useFirstReportLogic();

  // ハンドラー
  const handleNext = useCallback(async () => {
    try {
      // taskStatus が 13 または 14 の場合のみ更新処理
      if (taskStatus === 13 || taskStatus === 14) {
        const payload = {
          table_name: "report_progress",
          column_name1: taskStatus === 13 ? "ir_reported_at" : "ir_reported_to_internal_at",
          column_name2: taskStatus === 13 ? "ir_reported_by" : "ir_reported_to_internal_by",
          earthquake: earthquake,
          user_uuid: currentUser.id
        };
        await postData("api/post_update_report_progress", payload);
      }

      // taskStatus に応じた画面遷移やステータス変更
      if (taskStatus === 13) setTaskStatus(14);
      else if (taskStatus === 14) setTaskStatus(15);
      else if (taskStatus === 15 && earthquake?.uuid) {
        window.location.href = `/primary_inspection/${earthquake.uuid}`;
      }

    } catch (err) {
      console.error("更新失敗しました。：", err);
    } finally {
      // ダイアログを閉じる
      setOpen(false);
    }
  }, [taskStatus, earthquake, currentUser]);

  const showTitle = taskStatus === 12;
  const showApproval = approvalStatus !== APPROVAL_STATUS.INIT && taskStatus === 12;

  const damResult = useMemo(() => {
    const result = checkResult.filter(row => row.facility_category === 1);
    return result;
    // return checkResult.filter(row => row.facility_category === 1);
  }, [checkResult]);

  const processedCheckResult = useMemo(() => {
    const grouped = {};

    checkResult.forEach(row => {
      const uuid = row.facility_uuid;

      if (facilityDams.includes(uuid)) {
        // facilityDamsに含まれる場合は最大震度のレコードを保持
        if (!grouped[uuid] ||
          getIntensityPriority(row.intensity) > getIntensityPriority(grouped[uuid].intensity)) {
          grouped[uuid] = { ...row };
        }
      } else {
        // facilityDamsに含まれない場合はそのまま保持
        grouped[uuid] = { ...row };
      }
    });
    return Object.values(grouped);
  }, [checkResult, facilityDams]);

  if (!isValidTask) {
    return <ErrorScreen {...{ earthquake, open, onOpen: () => setOpen(true), onClose: () => setOpen(false), onRestart: handleRestart }} />;
  }

  if (loading) {
    return <SkeletonLoading />;
  }
  return (
    <BoxCenterStart sx={commonStyles.page}>
      <Grid container spacing={7} columns={12} sx={commonStyles.container}>

        {/* ヘッダー部分 */}
        {showTitle && (
          <Grid size={{ xs: 12 }}>
            <ReportStopDialog initial_report_uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} />
            <TypographyTitle>{earthquake.name}</TypographyTitle>
          </Grid>
        )}

        {showApproval && (
          <Grid size={{ xs: 12 }}>
            <TypographyBold>承認状況</TypographyBold>
            <CommonCard child={<ApprovalStatusInfo approval={approval} approvalStatus={approvalStatus} />} />
          </Grid>
        )}
        <DamReportStepper reportDetailList={reportDetailList} maxWidth={1100} />
        {/* 入力者モード */}
        {isInputter && (
          <>
            <FirstReportStep01 descriptions={FIRST_REPORT_TEXTS.inputter.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <FirstReportStep02 title={FIRST_REPORT_TEXTS.inputter.step02.title} lead={FIRST_REPORT_TEXTS.inputter.step02.lead} descriptions={FIRST_REPORT_TEXTS.inputter.step02.descriptions} checkResult={damResult} setCheckResult={setCheckResult} isReadOnly={isReadOnly} facilityDams={facilityDams} />
            <FirstReportStep03 title={FIRST_REPORT_TEXTS.inputter.step03.title} lead={FIRST_REPORT_TEXTS.inputter.step03.lead} descriptions={FIRST_REPORT_TEXTS.inputter.step03.descriptions} checkResult={processedCheckResult} setCheckResult={setCheckResult} isReadOnly={isReadOnly} facilityDams={facilityDams} />
            <FirstReportStep04 title={FIRST_REPORT_TEXTS.inputter.step04.title} lead={FIRST_REPORT_TEXTS.inputter.step04.lead} descriptions={FIRST_REPORT_TEXTS.inputter.step04.descriptions} checkResult={checkResult} setCheckResult={setCheckResult} earthquake={earthquake} checkSummary={{}} setCreateComplete={setCreateComplete} isCreateExcel={true} facilityDams={facilityDams} />
            <FirstReportStep05 title={FIRST_REPORT_TEXTS.inputter.step05.title} lead={FIRST_REPORT_TEXTS.inputter.step05.lead} earthquake={earthquake} checkResult={checkResult} createComplete={createComplete} isApproveRequest={true} facilityDams={facilityDams} />
          </>
        )}

        {/* レビュアーモード */}
        {isReviewer && (
          <>
            <FirstReportStep01 descriptions={FIRST_REPORT_TEXTS.reviewer.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <FirstReportStep02 title={FIRST_REPORT_TEXTS.reviewer.step02.title} lead={FIRST_REPORT_TEXTS.reviewer.step02.lead} descriptions={FIRST_REPORT_TEXTS.reviewer.step02.descriptions} checkResult={damResult} setCheckResult={setCheckResult} isReadOnly={isReadOnly} facilityDams={facilityDams} />
            <FirstReportStep03 title={FIRST_REPORT_TEXTS.reviewer.step03.title} lead={FIRST_REPORT_TEXTS.reviewer.step03.lead} descriptions={FIRST_REPORT_TEXTS.reviewer.step03.descriptions} checkResult={processedCheckResult} setCheckResult={setCheckResult} isReadOnly={isReadOnly} facilityDams={facilityDams} />
            <FirstReportStep04 title={FIRST_REPORT_TEXTS.reviewer.step04.title} lead={FIRST_REPORT_TEXTS.reviewer.step04.lead} earthquake={earthquake} isCreateExcel={false} facilityDams={facilityDams} />
            <FirstReportStep05 title={FIRST_REPORT_TEXTS.reviewer.step05.title} lead={FIRST_REPORT_TEXTS.reviewer.step05.lead} descriptions={FIRST_REPORT_TEXTS.reviewer.step05.descriptions} earthquake={earthquake} isApproveRequest={false} />
          </>
        )}

        {/* メール確認・送信 */}
        {isApprovedNotSent && (
          <>
            <Grid size={{ xs: 12 }}><MailConfirm isChecked={isChecked} setIsChecked={setIsChecked} mail_info={mailInfo} department={department} /></Grid>
            <Grid size={{ xs: 12 }}><SendReportDialog uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} taskStatus={taskStatus} setTaskStatus={setTaskStatus} disabled={!isChecked} type="initial" department={department} /></Grid>
          </>
        )}

        {/* 送信後タスク */}
        {isSent && taskStatus === 13 && <FirstReportAfterApprovalTask title={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status13.title} lead={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status13.lead} appealContent={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status13.appealContent} onClickRequestButton={() => setOpen(true)} />}
        {isSent && taskStatus === 14 &&
          <FirstReportAfterApprovalTask
            title={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status14.title}
            appealContent={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status14.appealContent}
            lead={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status14.lead}
            attachedFiles={[
              "様式１（20240701改正：新様式） 第１報.xlsx",
              "様式ダ−3（大分河川国道事務所殿）第１報.xlsx",
            ]}
            onClickRequestButton={() => setOpen(true)}
          />
        }
        {isSent && taskStatus === 15 && <FirstReportAfterApprovalTask title={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status15.title} lead={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status15.lead} buttonContent={FIRST_REPORT_POST_SEND_TASKS_TEXTS.status15.buttonContent} onClickRequestButton={() => setOpen(true)} />}

        <BaseDialog open={open} onClose={() => setOpen(false)} title="業務実施確認" message={`次の業務を実施します。\nよろしいですか？`} onConfirm={handleNext} confirmText="OK" cancelText="キャンセル" />
        <ReportCountdown occued_at={earthquake.occured_at} addedMinutes={REPORTING_TIME.FISRTREPORT} />
      </Grid>
    </BoxCenterStart>
  );
};

export default FirstReport;